import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Progress } from './ui/progress';
import {
  AlertCircle, Archive, Check, ChevronDown, ChevronRight, FileSearch,
  FileText, FolderOpen, FolderUp, Loader2, RefreshCw, Sparkles, Trash2,
  Upload, XCircle,
} from 'lucide-react';

import DocumentTypePicker from './DocumentTypePicker';
import KnowledgeBadgePicker from './KnowledgeBadgePicker';
import KnowledgeKeywordGroupPicker from './KnowledgeKeywordGroupPicker';
const ACCEPTED = '.pdf,.docx,.xlsx,.txt,.md';
const ACTIVE_STATES = new Set(['uploaded', 'processing', 'extracting', 'publishing']);

const extensionOf = (path = '') => {
  const name = String(path).split('/').pop() || '';
  const index = name.lastIndexOf('.');
  return index >= 0 ? name.slice(index).toLowerCase() : '';
};

const isSilentlyIgnored = (path = '') => {
  const parts = String(path).replaceAll('\\', '/').split('/').filter(Boolean);
  const name = (parts.at(-1) || '').toLowerCase();
  return parts.some(part => part.startsWith('.'))
    || ['.ds_store', 'thumbs.db', 'desktop.ini'].includes(name)
    || name.startsWith('~$')
    || ['.tmp', '.temp', '.swp'].some(suffix => name.endsWith(suffix));
};

const readDirectoryEntries = async (reader) => {
  const entries = [];
  while (true) {
    const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
    if (!batch.length) return entries;
    entries.push(...batch);
  }
};

const walkDroppedEntry = async (entry, parentPath = '') => {
  if (!entry) return [];
  const relativePath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    return [{ file, relativePath }];
  }
  if (!entry.isDirectory) return [];
  const children = await readDirectoryEntries(entry.createReader());
  const nested = await Promise.all(children.map(child => walkDroppedEntry(child, relativePath)));
  return nested.flat();
};

const collectDroppedFiles = async (dataTransfer) => {
  const items = Array.from(dataTransfer?.items || []);
  const entries = items.map(item => item.webkitGetAsEntry?.()).filter(Boolean);
  if (entries.length) {
    const nested = await Promise.all(entries.map(entry => walkDroppedEntry(entry)));
    return nested.flat();
  }
  return Array.from(dataTransfer?.files || []).map(file => ({
    file,
    relativePath: file.webkitRelativePath || file.name,
  }));
};

const scoreStyle = (score = 0) => score >= 0.9
  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
  : score >= 0.7
    ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-rose-50 text-rose-700 border-rose-200';

const statusStyle = (status) => ({
  ready: 'bg-emerald-50 text-emerald-700',
  published: 'bg-emerald-50 text-emerald-700',
  needs_review: 'bg-amber-50 text-amber-700',
  failed: 'bg-rose-50 text-rose-700',
  blocked: 'bg-rose-50 text-rose-700',
  extracting: 'bg-blue-50 text-blue-700',
  publishing: 'bg-blue-50 text-blue-700',
}[status] || 'bg-slate-100 text-slate-600');

const statusLabel = (status) => ({
  uploaded: 'Queued',
  extracting: 'Reading and classifying',
  needs_review: 'Ready for review',
  ready: 'Classified',
  publishing: 'Publishing',
  published: 'Published',
  failed: 'Failed',
  blocked: 'Needs attention',
  expired: 'Expired',
  cancelled: 'Cancelled',
}[status] || String(status || 'Queued').replaceAll('_', ' '));

const fieldValue = (field) => {
  const value = field?.value;
  if (Array.isArray(value)) return value.join(', ');
  if (value === null || value === undefined || value === '') return 'Not set';
  return String(value);
};

const KnowledgeIntake = ({
  user,
  onPublished,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}) => {
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [feature, setFeature] = useState(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen === undefined ? internalOpen : controlledOpen;
  const setOpen = useCallback((nextOpen) => {
    if (controlledOpen === undefined) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }, [controlledOpen, onOpenChange]);
  const [files, setFiles] = useState([]);
  const [sourceKind, setSourceKind] = useState('files');
  const [includeSubfolders, setIncludeSubfolders] = useState(true);
  const [useFolderHints, setUseFolderHints] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState('idle');
  const [batch, setBatch] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [showAutoFilled, setShowAutoFilled] = useState(false);
  const [inlineMessage, setInlineMessage] = useState('');
  const [loadingBatch, setLoadingBatch] = useState(false);
  const [savingDecision, setSavingDecision] = useState('');
  const [editingAutoField, setEditingAutoField] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [customizingItemId, setCustomizingItemId] = useState('');
  const [bulkDocumentType, setBulkDocumentType] = useState('');
  const [bulkBadgeIds, setBulkBadgeIds] = useState([]);
  const [bulkKeywordGroupIds, setBulkKeywordGroupIds] = useState([]);
  const [savingBulkField, setSavingBulkField] = useState('');
  const [publishResult, setPublishResult] = useState(null);
  const [showPublishReport, setShowPublishReport] = useState(false);
  const [discardIntent, setDiscardIntent] = useState(null);
  const [discarding, setDiscarding] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/knowledge/intake/status');
      setFeature(response.data);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) setFeature(null);
      else console.warn('[KnowledgeIntake] Could not load feature status', error);
      return null;
    }
  }, []);

  const loadBatch = useCallback(async (batchId, quiet = false) => {
    if (!batchId) return;
    if (!quiet) setLoadingBatch(true);
    try {
      const [batchResponse, decisionsResponse] = await Promise.all([
        axiosInstance.get(`/knowledge/intake/batches/${batchId}`),
        axiosInstance.get(`/knowledge/intake/batches/${batchId}/decisions`),
      ]);
      setBatch(batchResponse.data);
      setDecisions(decisionsResponse.data.decisions || []);
      setDrafts(previous => {
        const next = { ...previous };
        for (const decision of decisionsResponse.data.decisions || []) {
          if (!(decision.id in next) && decision.kind === 'field') {
            next[decision.id] = Array.isArray(decision.value)
              ? decision.value.join(', ')
              : (decision.value ?? '');
          }
        }
        return next;
      });
    } catch (error) {
      setInlineMessage(error?.response?.data?.detail || 'Qflow could not load this intake batch.');
    } finally {
      if (!quiet) setLoadingBatch(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const resume = async () => {
      setInlineMessage('');
      const latest = await loadStatus();
      if (!cancelled && latest?.active_batch_id) await loadBatch(latest.active_batch_id);
    };
    resume();
    return () => { cancelled = true; };
  }, [open, loadBatch, loadStatus]);

  useEffect(() => {
    if (!open || !batch?.id || !ACTIVE_STATES.has(batch.status)) return undefined;
    let delay = 2000;
    let cancelled = false;
    let timer;
    const poll = async () => {
      if (cancelled) return;
      await loadBatch(batch.id, true);
      delay = Math.min(Math.round(delay * 1.35), 8000);
      timer = setTimeout(poll, delay);
    };
    timer = setTimeout(poll, delay);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, batch?.id, batch?.status, loadBatch]);

  const openWorkflow = () => {
    setOpen(true);
  };

  const selectEntries = (entries, nextSourceKind = 'files') => {
    const next = Array.from(entries || []).filter(entry => !isSilentlyIgnored(entry.relativePath));
    setFiles(next);
    setSourceKind(nextSourceKind);
    setInlineMessage('');
  };

  const selectFiles = (selected, nextSourceKind = 'files') => {
    const entries = Array.from(selected || []).map(file => ({
      file,
      relativePath: file.webkitRelativePath || file.name,
    }));
    selectEntries(entries, nextSourceKind);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setDragActive(false);
    try {
      const entries = await collectDroppedFiles(event.dataTransfer);
      const folderDrop = entries.some(entry => entry.relativePath.includes('/'));
      selectEntries(entries, folderDrop ? 'folder' : 'files');
    } catch (error) {
      console.warn('[KnowledgeIntake] Could not read dropped folder', error);
      setInlineMessage('Qflow could not read that dropped folder. Use Choose folder and try again.');
    }
  };

  const selectionError = () => {
    const limits = feature?.limits || {};
    const maxFiles = limits.max_files_per_batch || 50;
    const maxFileMb = limits.max_file_size_mb || 20;
    const maxBatchMb = limits.max_batch_size_mb || 200;
    const maxDepth = limits.max_folder_depth || 8;
    const supported = new Set(limits.allowed_extensions || ACCEPTED.split(','));
    if (files.length > maxFiles) return `Choose no more than ${maxFiles} files in one batch.`;
    const eligible = files.filter(entry => supported.has(extensionOf(entry.relativePath)));
    const oversized = eligible.find(entry => entry.file.size > maxFileMb * 1024 * 1024);
    if (oversized) return `${oversized.file.name} is larger than the ${maxFileMb} MB file limit.`;
    const total = eligible.reduce((sum, entry) => sum + entry.file.size, 0);
    if (total > maxBatchMb * 1024 * 1024) return `This selection exceeds the ${maxBatchMb} MB batch limit.`;
    if (sourceKind === 'folder') {
      const tooDeep = files.find(entry => Math.max(0, entry.relativePath.split('/').length - 2) > maxDepth);
      if (tooDeep) return `Folder nesting exceeds the maximum depth of ${maxDepth}.`;
    }
    return '';
  };

  const submitFiles = async () => {
    if (!files.length) {
      setInlineMessage('Choose at least one supported document.');
      return;
    }
    const clientError = selectionError();
    if (clientError) {
      setInlineMessage(clientError);
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadPhase('uploading');
    setInlineMessage('');
    try {
      const form = new FormData();
      files.forEach(entry => form.append('files', entry.file, entry.file.name));
      form.append('source_kind', sourceKind);
      form.append('upload_mode', 'auto');
      form.append('relative_paths', JSON.stringify(files.map(entry => entry.relativePath)));
      form.append('include_subfolders', String(includeSubfolders));
      form.append('use_folder_category_hints', String(useFolderHints));
      const response = await axiosInstance.post('/knowledge/intake/batches', form, {
        onUploadProgress: (event) => {
          if (!event.total) return;
          const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
          setUploadProgress(percent);
          if (percent >= 100) setUploadPhase('creating');
        },
      });
      setFiles([]);
      await loadBatch(response.data.id);
      await loadStatus();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setInlineMessage(typeof detail === 'string' ? detail : detail?.message || 'Qflow could not start this intake batch.');
    } finally {
      setUploading(false);
      setUploadPhase('idle');
    }
  };

  const decideField = async (decision, value = drafts[decision.id], action = 'confirm') => {
    let finalValue = value;
    if (['badges', 'keyword_groups'].includes(decision.field_key) && typeof finalValue === 'string') {
      finalValue = finalValue.split(',').map(item => item.trim()).filter(Boolean);
    }
    try {
      await axiosInstance.patch(`/knowledge/intake/items/${decision.item_id}/fields/${decision.field_key}`, { value: finalValue === '' ? null : finalValue, action });
      await loadBatch(batch.id, true);
      return true;
    } catch (error) {
      setInlineMessage(error?.response?.data?.detail || 'This decision could not be saved.');
      return false;
    }
  };

  const decideFolderField = async (group, value = null, decideFileByFile = false) => {
    setSavingDecision(group.id);
    try {
      await axiosInstance.patch(`/knowledge/intake/batches/${batch.id}/bulk-decisions`, {
        folder_path: group.folderPath,
        field_key: group.fieldKey,
        value,
        action: value === group.value ? 'confirm' : 'edit',
        decide_file_by_file: decideFileByFile,
      });
      await loadBatch(batch.id, true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setInlineMessage(typeof detail === 'string' ? detail : detail?.message || 'This folder decision could not be saved.');
    } finally {
      setSavingDecision('');
    }
  };

  const decideVersion = async (decision, value) => {
  const applyBatchField = async (fieldKey, value, label) => {
    if (!batch?.id) return;
    setSavingBulkField(fieldKey);
    try {
      await axiosInstance.patch(`/knowledge/intake/batches/${batch.id}/bulk-decisions`, {
        folder_path: '',
        field_key: fieldKey,
        value,
        action: 'edit',
        decide_file_by_file: false,
      });
      await loadBatch(batch.id, true);
      toast.success(`${label} applied to this upload`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setInlineMessage(typeof detail === 'string' ? detail : detail?.message || `${label} could not be applied.`);
    } finally {
      setSavingBulkField('');
    }
  };

  const saveItemField = async (item, fieldKey, value) => {
    setSavingDecision(`${item.id}:${fieldKey}`);
    try {
      await axiosInstance.patch(`/knowledge/intake/items/${item.id}/fields/${fieldKey}`, { value, action: 'edit' });
      await loadBatch(batch.id, true);
      toast.success(`${item.filename} updated`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setInlineMessage(typeof detail === 'string' ? detail : detail?.message || 'This file could not be updated.');
    } finally {
      setSavingDecision('');
    }
  };

    try {
      await axiosInstance.patch(`/knowledge/intake/items/${decision.item_id}/version-decision`, { decision: value });
      await loadBatch(batch.id, true);
    } catch (error) {
      setInlineMessage(error?.response?.data?.detail || 'This version decision could not be saved.');
    }
  };

  const retryItem = async (itemId) => {
    try {
      await axiosInstance.post(`/knowledge/intake/items/${itemId}/retry`);
      await loadBatch(batch.id, true);
    } catch (error) {
      setInlineMessage(error?.response?.data?.detail || 'This file could not be retried.');
    }
  };

  const removeItem = async (itemId) => {
    try {
      await axiosInstance.delete(`/knowledge/intake/items/${itemId}`);
      await loadBatch(batch.id, true);
    } catch (error) {
      setInlineMessage(error?.response?.data?.detail || 'This file could not be removed.');
    }
  };

  const publish = async () => {
    if ((batch?.counts?.decisions || 0) > 0) {
      setInlineMessage(`Resolve ${batch.counts.decisions} remaining decision${batch.counts.decisions === 1 ? '' : 's'} before publishing.`);
      return;
    }
    setPublishing(true);
    try {
      const response = await axiosInstance.post(`/knowledge/intake/batches/${batch.id}/publish`, {});
      const replacements = (batch.items || []).filter(item => item.version_match?.decision === 'replace').length;
      setPublishResult({
        ...response.data,
        replacements,
        newDocuments: Math.max(0, (response.data.published?.length || 0) - replacements),
      });
      setShowPublishReport(false);
      await loadStatus();
      if (response.data.failures?.length) setInlineMessage(`${response.data.published.length} published; ${response.data.failures.length} need attention.`);
      else {
        toast.success(`${response.data.published.length} document${response.data.published.length === 1 ? '' : 's'} published and indexed`);
      }
      onPublished?.();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setInlineMessage(typeof detail === 'string' ? detail : detail?.message || 'Qflow could not publish this batch.');
    } finally {
      setPublishing(false);
    }
  };

  const resetPublishedFlow = () => {
    setPublishResult(null);
    setShowPublishReport(false);
    setBatch(null);
    setDecisions([]);
    setDrafts({});
    setFiles([]);
    setShowAutoFilled(false);
    setEditingAutoField('');
    setInlineMessage('');
  };

  const publishInProgress = publishing || batch?.status === 'publishing';
  const hasPublishedItems = (batch?.counts?.published || 0) > 0;
  const batchCanBeDiscarded = Boolean(
    batch?.id
    && !publishResult
    && !publishInProgress
    && !hasPublishedItems
    && !['published', 'cancelled', 'expired'].includes(batch.status),
  );

  const requestDialogClose = (nextOpen) => {
    if (nextOpen) {
      setOpen(true);
      return;
    }
    if (uploading || publishInProgress) {
      setInlineMessage(
        publishing || batch?.status === 'publishing'
          ? 'Publishing is in progress. Keep this window open until Qflow confirms the result.'
          : 'Please wait until Qflow confirms the upload before closing this window.',
      );
      return;
    }
    if (batchCanBeDiscarded) {
      setDiscardIntent('close');
      return;
    }
    if (publishResult) resetPublishedFlow();
    else {
      setFiles([]);
      setInlineMessage('');
    }
    setOpen(false);
  };

  const requestStartOver = () => {
    if (publishInProgress) {
      setInlineMessage('Publishing is in progress. This upload cannot be discarded now.');
      return;
    }
    if (hasPublishedItems) {
      setInlineMessage('Some files from this upload are already published. Retry or remove the remaining failed files; published knowledge must be managed in Knowledge Hub.');
      return;
    }
    if (batchCanBeDiscarded) {
      setDiscardIntent('restart');
      return;
    }
    resetPublishedFlow();
  };

  const keepBatchForLater = () => {
    setDiscardIntent(null);
    setOpen(false);
  };

  const discardBatch = async () => {
    if (!batch?.id || discarding || publishInProgress) return;
    const shouldClose = discardIntent === 'close';
    setDiscarding(true);
    try {
      await axiosInstance.post(`/knowledge/intake/batches/${batch.id}/cancel`);
      resetPublishedFlow();
      await loadStatus();
      setDiscardIntent(null);
      if (shouldClose) setOpen(false);
      else setInlineMessage('Previous upload discarded. You can start a new upload.');
    } catch (error) {
      setDiscardIntent(null);
      setInlineMessage(error?.response?.data?.detail || 'This saved upload could not be discarded. Please try again.');
    } finally {
      setDiscarding(false);
    }
  };

  const askNextTime = async () => {
    try {
      await axiosInstance.put('/knowledge/intake/upload-mode', { knowledge_upload_mode: 'ask' });
      await loadStatus();
      toast.success('Qflow will ask which upload mode to use next time');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Your upload preference could not be changed.');
    }
  };

  const autoFilled = useMemo(() => (batch?.items || []).flatMap(item =>
    Object.entries(item.fields || {})
      .filter(([, field]) => field.status === 'auto_accepted' && field.value !== null && field.value !== '' && (!Array.isArray(field.value) || field.value.length))
      .map(([key, field]) => ({ item, key, field })),
  ), [batch]);

  const batchFileCount = batch?.items?.length || batch?.counts?.documents_read || 0;
  const processedFileCount = batch?.counts?.documents_read || 0;
  const autoFilledCount = batch?.counts?.auto_filled ?? autoFilled.length;
  const remainingDecisionCount = batch?.counts?.decisions ?? decisions.length;

  const selectionSummary = useMemo(() => {
    const allowed = new Set(feature?.limits?.allowed_extensions || ACCEPTED.split(','));
    const skipped = files.filter(entry => !allowed.has(extensionOf(entry.relativePath)));
    const accepted = files.filter(entry => allowed.has(extensionOf(entry.relativePath)));
    const roots = new Set(accepted.map(entry => entry.relativePath.split('/')[0]).filter(Boolean));
    const rootName = sourceKind === 'folder' && roots.size === 1 ? Array.from(roots)[0] : '';
    const groups = new Map();
    accepted.forEach(entry => {
      const parts = entry.relativePath.split('/');
      const folderParts = parts.slice(0, -1);
      if (rootName && folderParts[0] === rootName) folderParts.shift();
      const folder = folderParts.join('/') || 'Top level';
      groups.set(folder, (groups.get(folder) || 0) + 1);
    });
    return {
      accepted,
      skipped,
      rootName,
      groups: Array.from(groups.entries()).map(([folder, count]) => ({ folder, count })),
      totalSize: accepted.reduce((sum, entry) => sum + entry.file.size, 0),
    };
  }, [feature?.limits?.allowed_extensions, files, sourceKind]);

  const processingSummary = useMemo(() => {
    const items = batch?.items || [];
    const completeStates = new Set(['ready', 'needs_review', 'failed', 'blocked', 'published']);
    const completed = items.filter(item => completeStates.has(item.status)).length;
    const reading = items.filter(item => item.status === 'extracting').length;
    const queued = items.filter(item => item.status === 'uploaded').length;
    const total = batch?.counts?.total || items.length;
    return {
      total,
      completed,
      reading,
      queued,
      percent: total ? Math.min(100, Math.round((completed / total) * 100)) : 0,
    };
  }, [batch]);

  const decisionQueue = useMemo(() => {
    const itemById = new Map((batch?.items || []).map(item => [item.id, item]));
    const grouped = new Map();
    const individual = [];
    decisions.forEach(decision => {
      if (decision.kind !== 'field') {
        individual.push(decision);
        return;
      }
      const item = itemById.get(decision.item_id);
      const field = item?.fields?.[decision.field_key] || {};
      if (field.decision_scope === 'folder_file_by_file') {
        individual.push(decision);
        return;
      }
      const folderPath = decision.field_key === 'access' ? '' : (item?.folder_path || '');
      const canGroup = decision.field_key === 'access' || ['owner_team', 'category'].includes(decision.field_key);
      if (!canGroup) {
        individual.push(decision);
        return;
      }
      const signature = decision.field_key === 'access' ? 'batch' : JSON.stringify(decision.value ?? null);
      const key = `${folderPath}:${decision.field_key}:${signature}`;
      if (!grouped.has(key)) grouped.set(key, {
        id: `folder:${key}`,
        folderPath,
        fieldKey: decision.field_key,
        label: decision.label,
        value: decision.value,
        candidates: decision.candidates || [],
        decisions: [],
      });
      grouped.get(key).decisions.push(decision);
    });
    const groups = [];
    grouped.forEach(group => {
      if (group.decisions.length > 1 || group.fieldKey === 'access') groups.push(group);
      else individual.push(group.decisions[0]);
    });
    return { groups, individual };
  }, [batch, decisions]);

  if (!feature) return null;

  const renderDecision = (decision) => {
    if (decision.kind === 'version') {
      return (
        <div key={decision.id} className="rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid="intake-version-decision">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="font-medium text-slate-900">Possible newer version</p>
              <p className="text-xs text-slate-500">{decision.filename}</p>
            </div>
            <span className="rounded-full border border-amber-200 bg-white px-2 py-1 text-xs text-amber-700">{Math.round((decision.similarity || 0) * 100)}% match</span>
          </div>
          <p className="mt-3 text-sm text-slate-700">
            This resembles <strong>{decision.candidate_title || 'an existing document'}</strong>. Replacing keeps the old version for audit but removes it from answers.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => decideVersion(decision, 'replace')}><Archive size={14} /> Replace old version</Button>
            <Button size="sm" variant="outline" onClick={() => decideVersion(decision, 'keep_both')}>Keep both</Button>
            <Button size="sm" variant="ghost" onClick={() => decideVersion(decision, 'new')}>Treat as new</Button>
          </div>
        </div>
      );
    }

    return (
      <div key={decision.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid="intake-field-decision">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="font-medium text-slate-900">{decision.label}</p>
            <p className="mt-0.5 text-xs text-slate-500">{decision.filename}{decision.evidence?.page ? ` · Page ${decision.evidence.page}` : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600">{decision.reason}</span>
            <span className={`rounded-full border px-2 py-1 text-xs ${scoreStyle(decision.score)}`}>{Math.round((decision.score || 0) * 100)}%</span>
          </div>
        </div>
        {decision.evidence?.snippet && (
          <blockquote className="mt-3 rounded-lg border-l-4 border-sky-300 bg-sky-50 px-3 py-2 text-sm text-slate-700">
            <mark className="bg-amber-100 text-inherit">{decision.evidence.snippet}</mark>
          </blockquote>
        )}
        {!!decision.candidates?.length && (
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {decision.candidates.map((candidate, index) => {
              const value = candidate.value ?? candidate.label;
              const selected = String(drafts[decision.id] ?? '') === String(value ?? '');
              return (
                <button
                  type="button"
                  key={`${decision.id}-${value}-${index}`}
                  className={`rounded-lg border px-3 py-2 text-left text-sm ${selected ? 'border-sky-500 bg-sky-50 text-sky-900' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                  onClick={() => setDrafts(previous => ({ ...previous, [decision.id]: value ?? '' }))}
                >
                  <span className="font-medium">{candidate.label || value || 'No value'}</span>
                  {index === 0 && <span className="ml-2 text-xs text-sky-700">Suggested</span>}
                </button>
              );
            })}
          </div>
        )}
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input
            className="h-9 min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 text-sm"
            value={drafts[decision.id] ?? ''}
            onChange={event => setDrafts(previous => ({ ...previous, [decision.id]: event.target.value }))}
            placeholder="Enter another value"
            aria-label={`Value for ${decision.label}`}
          />
          <Button size="sm" onClick={() => decideField(decision, drafts[decision.id], drafts[decision.id] === decision.value ? 'confirm' : 'edit')}>
            <Check size={14} /> Confirm
          </Button>
        </div>
      </div>
    );
  };

  const renderFolderDecision = (group) => {
    const fallbackOptions = group.fieldKey === 'access'
      ? [
        { value: 'restricted', label: 'Restricted to each document’s owner team' },
        { value: 'public', label: 'Visible to everyone in this organisation' },
      ]
      : [];
    const rawOptions = group.candidates?.length
      ? group.candidates
      : (fallbackOptions.length ? fallbackOptions : [{ value: group.value, label: fieldValue({ value: group.value }) }]);
    const seen = new Set();
    const options = rawOptions.filter(option => {
      const value = option.value ?? option.label;
      const key = JSON.stringify(value);
      if (value === undefined || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const folderLabel = group.folderPath || 'this batch';
    const title = group.fieldKey === 'access'
      ? (group.decisions.length === 1 ? 'Who can access this file?' : `Who can access these ${group.decisions.length} files?`)
      : `${group.label} for ${folderLabel}`;
    const busy = savingDecision === group.id;

    return (
      <div key={group.id} className="rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="intake-folder-decision">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="flex items-center gap-2 font-medium text-slate-900"><FolderOpen size={17} className="text-amber-600" /> {title}</p>
            <p className="mt-1 text-xs text-slate-500">Applies to {group.decisions.length} file{group.decisions.length === 1 ? '' : 's'}{group.folderPath ? ' in this folder and its subfolders' : ''}.</p>
          </div>
          {group.fieldKey === 'access' && <span className="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-medium text-amber-800">Always asked</span>}
        </div>
        <div className="mt-3 grid gap-2">
          {options.map((option, index) => {
            const value = option.value ?? option.label;
            return (
              <button
                type="button"
                key={`${group.id}:${JSON.stringify(value)}`}
                className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left text-sm text-slate-800 hover:border-sky-300 hover:bg-sky-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 disabled:opacity-60"
                onClick={() => decideFolderField(group, value, false)}
                disabled={busy}
              >
                <span className="h-4 w-4 shrink-0 rounded-full border border-slate-400 bg-white" aria-hidden="true" />
                <span className="min-w-0 flex-1 font-medium">{option.label || fieldValue({ value })}</span>
                {index === 0 && <span className="text-xs text-sky-700">Suggested</span>}
              </button>
            );
          })}
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left text-sm font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 disabled:opacity-60"
            onClick={() => decideFolderField(group, null, true)}
            disabled={busy}
          >
            <span className="h-4 w-4 shrink-0 rounded-full border border-slate-400 bg-white" aria-hidden="true" />
            Decide file by file
            {busy && <Loader2 size={14} className="ml-auto animate-spin" />}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {!hideTrigger && (
        <div className="relative inline-flex">
          <Button
            variant="outline"
            size="sm"
            className="border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100"
            onClick={openWorkflow}
            data-testid="knowledge-intake-button"
          >
            <Sparkles size={14} /> Upload and auto-classify
          </Button>
          {(feature.unresolved_decisions || 0) > 0 && (
            <span className="absolute -right-2 -top-2 min-w-5 rounded-full bg-amber-500 px-1.5 py-0.5 text-center text-[10px] font-medium text-white" aria-label={`${feature.unresolved_decisions} intake decisions`}>
              {feature.unresolved_decisions}
            </span>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={requestDialogClose}>
        <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto p-0" data-testid="knowledge-intake-dialog">
          <DialogHeader className="border-b border-slate-200 px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-4 pr-8">
              <div>
                <DialogTitle className="flex items-center gap-2 text-xl"><Sparkles className="text-sky-600" /> Upload and auto-classify</DialogTitle>
                <DialogDescription className="mt-1 text-sm text-slate-500">Qflow fills supported metadata locally and asks only for uncertain or protected decisions.</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {publishResult ? (
            <div className="space-y-5 p-6" data-testid="intake-publish-confirmation">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 px-5 py-8 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Check size={27} /></div>
                <h3 className="mt-4 text-xl font-semibold text-slate-950">
                  {publishResult.published?.length || 0} document{publishResult.published?.length === 1 ? '' : 's'} published
                </h3>
                <p className="mt-1 text-sm text-slate-600">Published and searchable by Qflow</p>
                <p className="mt-3 text-xs text-slate-500">
                  {publishResult.newDocuments} new · {publishResult.replacements} replaced a previous version · access applied · audit entries written
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  <Button onClick={() => { setOpen(false); resetPublishedFlow(); onPublished?.(); }}>Open Knowledge Hub</Button>
                  <Button variant="outline" onClick={() => setShowPublishReport(value => !value)}>{showPublishReport ? 'Hide batch report' : 'View batch report'}</Button>
                  <Button variant="ghost" onClick={resetPublishedFlow}>Upload another batch</Button>
                </div>
              </div>

              {!!publishResult.failures?.length && (
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
                  <AlertCircle size={18} className="mt-0.5 shrink-0" />
                  <div><p className="font-medium">Some documents were not published</p><p className="mt-1 text-xs">The successful documents are searchable. Review the batch report for the remaining files and reasons.</p></div>
                </div>
              )}

              {showPublishReport && (
                <section className="overflow-hidden rounded-xl border border-slate-200" data-testid="intake-batch-report">
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                    <h4 className="font-medium text-slate-900">Batch report</h4>
                    <p className="text-xs text-slate-500">Batch {publishResult.batch_id}</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {(publishResult.published || []).map(document => (
                      <div key={document.id} className="flex items-center gap-3 px-4 py-3 text-sm"><Check size={16} className="text-emerald-600" /><span className="min-w-0 flex-1 truncate text-slate-800">{document.title}</span><span className="text-xs text-emerald-700">Published</span></div>
                    ))}
                    {(publishResult.failures || []).map(failure => (
                      <div key={failure.item_id} className="flex items-start gap-3 px-4 py-3 text-sm"><XCircle size={16} className="mt-0.5 text-rose-600" /><div className="min-w-0 flex-1"><p className="truncate text-slate-800">{failure.filename}</p><p className="mt-0.5 text-xs text-rose-700">{failure.message}</p></div><span className="text-xs text-rose-700">Failed</span></div>
                    ))}
                  </div>
                </section>
              )}

              {!feature.upload_mode?.locked && (
                <p className="text-center text-xs text-slate-500">
                  {feature.upload_mode?.knowledge_upload_mode === 'auto' ? 'Auto-classify is your default for next time.' : 'Your upload preference is saved.'}{' '}
                  <button type="button" className="font-medium text-sky-700 underline underline-offset-2" onClick={askNextTime}>Ask me next time</button>
                </p>
              )}
            </div>
          ) : !batch ? (
            <div className="space-y-5 p-6">
              <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-slate-200 text-xs sm:grid-cols-4">
                <div className="bg-sky-50 px-3 py-2 text-sky-800">1. Upload</div>
                <div className="px-3 py-2 text-slate-500">2. Extract</div>
                <div className="px-3 py-2 text-slate-500">3. Review suggestions</div>
                <div className="px-3 py-2 text-slate-500">4. Confirm and index</div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" role="group" aria-label="Choose files or a folder">
                  <button type="button" onClick={() => setSourceKind('files')} className={`inline-flex h-8 items-center gap-2 rounded-md px-3 text-sm font-medium ${sourceKind === 'files' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`} aria-pressed={sourceKind === 'files'}>
                    <FileText size={15} /> Files
                  </button>
                  <button type="button" onClick={() => setSourceKind('folder')} className={`inline-flex h-8 items-center gap-2 rounded-md px-3 text-sm font-medium ${sourceKind === 'folder' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`} aria-pressed={sourceKind === 'folder'}>
                    <FolderOpen size={15} /> Folder
                  </button>
                </div>
                <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700"><Sparkles size={12} className="mr-1 inline" /> Auto-classify</span>
              </div>
              <div
                className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragActive ? 'border-sky-500 bg-sky-50' : 'border-slate-300 bg-slate-50/60'}`}
                onDragEnter={event => { event.preventDefault(); setDragActive(true); }}
                onDragOver={event => event.preventDefault()}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
              >
                {sourceKind === 'folder' ? <FolderUp className="mx-auto text-sky-600" size={30} /> : <Upload className="mx-auto text-sky-600" size={30} />}
                <p className="mt-3 font-medium text-slate-900">Drop {sourceKind === 'folder' ? 'a folder' : 'documents'} here</p>
                <p className="mt-1 text-sm text-slate-500">
                  PDF, DOCX, XLSX, TXT or MD · up to {feature.limits?.max_file_size_mb || 20} MB each · {feature.limits?.max_files_per_batch || 50} files per batch
                </p>
                <Button className="mt-4" variant="outline" onClick={() => (sourceKind === 'folder' ? folderInputRef : fileInputRef).current?.click()}>
                  {sourceKind === 'folder' ? 'Choose folder' : 'Choose files'}
                </Button>
                <input ref={fileInputRef} type="file" multiple accept={ACCEPTED} className="hidden" onChange={event => selectFiles(event.target.files, 'files')} />
                <input ref={folderInputRef} type="file" webkitdirectory="" directory="" multiple className="hidden" onChange={event => selectFiles(event.target.files, 'folder')} />
              </div>
              {!!files.length && (
                <div className="rounded-xl border border-slate-200">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
                    <div>
                      <p className="flex items-center gap-2 text-sm font-medium text-slate-800">
                        {sourceKind === 'folder' ? <FolderOpen size={16} className="text-sky-600" /> : <FileText size={16} className="text-sky-600" />}
                        {selectionSummary.rootName || 'Selected documents'}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">{selectionSummary.accepted.length} supported · {(selectionSummary.totalSize / 1024 / 1024).toFixed(1)} MB{sourceKind === 'folder' ? ` · ${selectionSummary.groups.filter(group => group.folder !== 'Top level').length} subfolders` : ''}</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setFiles([])}><Trash2 size={14} /> Clear</Button>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {sourceKind === 'folder' ? selectionSummary.groups.map(group => (
                      <div key={group.folder} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                        <FolderOpen size={15} className="text-sky-600" />
                        <span className="min-w-0 flex-1 truncate font-medium text-slate-700">{group.folder}</span>
                        <span className="text-xs text-slate-500">{group.count} file{group.count === 1 ? '' : 's'}</span>
                        {useFolderHints && group.folder !== 'Top level' && <span className="hidden rounded-full bg-sky-50 px-2 py-1 text-[11px] text-sky-700 sm:inline">Category hint</span>}
                      </div>
                    )) : selectionSummary.accepted.map((entry, index) => (
                      <div key={`${entry.relativePath}-${index}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                        <FileText size={16} className="text-sky-600" />
                        <span className="min-w-0 flex-1 truncate">{entry.file.name}</span>
                        <span className="text-xs text-slate-500">{(entry.file.size / 1024 / 1024).toFixed(2)} MB</span>
                      </div>
                    ))}
                    {!!selectionSummary.skipped.length && (
                      <div className="flex items-start gap-3 bg-amber-50/70 px-4 py-3 text-xs text-amber-900">
                        <AlertCircle size={15} className="mt-0.5 shrink-0" />
                        <span><strong>{selectionSummary.skipped.length} will be skipped:</strong> unsupported {Array.from(new Set(selectionSummary.skipped.map(entry => extensionOf(entry.relativePath) || 'files without an extension'))).join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {sourceKind === 'folder' && (
                <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap gap-x-5 gap-y-2">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={includeSubfolders} onChange={event => setIncludeSubfolders(event.target.checked)} className="h-4 w-4 rounded border-slate-300" /> Include subfolders</label>
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={useFolderHints} onChange={event => setUseFolderHints(event.target.checked)} className="h-4 w-4 rounded border-slate-300" /> Use folder names as category hints</label>
                  </div>
                  <span className="text-xs text-slate-500">Hints only · no categories are created</span>
                </div>
              )}
              {inlineMessage && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert">
                  <AlertCircle size={17} className="mt-0.5 shrink-0" /> {inlineMessage}
                </div>
              )}
              {uploading && (
                <div className="space-y-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3" aria-live="polite" data-testid="intake-upload-progress">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium text-sky-900">{uploadPhase === 'creating' ? 'Upload received — creating the saved batch' : 'Uploading securely'}</span>
                    <span className="tabular-nums text-sky-700">{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} aria-label={`Upload ${uploadProgress}% complete`} />
                  <p className="text-xs text-sky-800">Keep this window open until Qflow confirms the batch. Reading and classification will then continue in the background.</p>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-slate-500">Access starts restricted and is always confirmed before publishing.</p>
                <Button onClick={submitFiles} disabled={!files.length || uploading}>
                  {uploading ? <Loader2 className="animate-spin" /> : <FileSearch />} {uploading ? 'Uploading' : 'Read and classify'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-5 p-6">
              {ACTIVE_STATES.has(batch.status) && (
                <section className="space-y-3 rounded-xl border border-sky-200 bg-sky-50/60 p-4" aria-live="polite" data-testid="intake-processing-progress">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-sky-950">Reading {processingSummary.total} file{processingSummary.total === 1 ? '' : 's'}</h3>
                      <p className="mt-1 text-xs text-sky-800">{processingSummary.completed} done · {processingSummary.reading} reading · {processingSummary.queued} queued</p>
                    </div>
                    <Button size="sm" variant="ghost" className="text-slate-600" onClick={requestStartOver} disabled={publishInProgress || hasPublishedItems}>Discard upload</Button>
                  </div>
                  <Progress value={processingSummary.percent} aria-label={`${processingSummary.percent}% of files read`} />
                  <p className="flex items-center gap-2 text-xs text-sky-800"><Loader2 size={13} className="animate-spin" /> You can leave this page. The batch is saved and the Knowledge Hub badge will show when decisions are ready.</p>
                </section>
              )}
              <div className="grid gap-3 sm:grid-cols-3" aria-live="polite" aria-label={`${processedFileCount} files processed, ${autoFilledCount} details completed by Qflow, ${remainingDecisionCount} required confirmations`}>
                {[
                  ['Files processed', processedFileCount, `file${processedFileCount === 1 ? '' : 's'}`],
                  ['Details completed by Qflow', autoFilledCount, `field${autoFilledCount === 1 ? '' : 's'}`],
                  ['Required confirmations', remainingDecisionCount, `item${remainingDecisionCount === 1 ? '' : 's'}`],
                ].map(([label, value, unit]) => (
                  <div key={label} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
                    <p className="text-xs text-slate-500">{unit}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs">
                <span className="font-medium text-slate-700">Intake sources</span>
                <span className="rounded-full bg-sky-100 px-2 py-1 text-sky-700">Manual upload active</span>
                {['SharePoint folders', 'Drive folders', 'Email inbox', 'API'].map(source => (
                  <span key={source} className="rounded-full border border-slate-200 bg-white px-2 py-1 text-slate-400">{source} · coming soon</span>
                ))}
              </div>

              <div className="rounded-xl border border-slate-200">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
                  <div>
                    <p className="font-medium text-slate-900">Files in this upload</p>
                    <p className="text-xs text-slate-500">This unfinished upload is saved. Closing the window will let you keep it for later or discard it.</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => loadBatch(batch.id)} disabled={loadingBatch}>
                    <RefreshCw className={loadingBatch ? 'animate-spin' : ''} /> Refresh
                  </Button>
                </div>
                <div className="divide-y divide-slate-100">
                  {(batch.items || []).map(item => (
                    <div key={item.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center">
                      <FileText size={16} className="shrink-0 text-sky-600" />
                      <span className="min-w-0 flex-1 truncate text-sm text-slate-800">{item.filename}</span>
                      <span className={`w-fit rounded-full px-2 py-1 text-xs ${statusStyle(item.status)}`}>{statusLabel(item.status)}</span>
                      {['ready', 'needs_review'].includes(item.status) && <Button size="sm" variant="ghost" onClick={() => setCustomizingItemId(current => current === item.id ? '' : item.id)}>{customizingItemId === item.id ? 'Close' : 'Customise metadata'}</Button>}
                      {['failed', 'blocked'].includes(item.status) && (
                        <div className="flex items-center gap-1">
                          {item.error?.retryable && <Button size="sm" variant="outline" onClick={() => retryItem(item.id)}>Retry</Button>}
                          <Button size="icon" variant="ghost" aria-label={`Remove ${item.filename}`} onClick={() => removeItem(item.id)}><Trash2 size={14} /></Button>
                        </div>
                      )}
                      {item.error?.message && <p className="w-full text-xs text-rose-700 sm:pl-7">{item.error.message}</p>}
                      {customizingItemId === item.id && (
                        <div className="w-full space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:ml-7">
                          <p className="text-xs font-medium text-slate-700">Choices here apply only to {item.filename}.</p>
                          <DocumentTypePicker value={item.fields?.document_type?.value || ''} onChange={nextValue => saveItemField(item, 'document_type', nextValue)} compact />
                          <KnowledgeBadgePicker value={item.fields?.badges?.value || []} onChange={nextValue => saveItemField(item, 'badges', nextValue)} user={user} compact />
                          <KnowledgeKeywordGroupPicker value={item.fields?.keyword_groups?.value || []} onChange={nextValue => saveItemField(item, 'keyword_groups', nextValue)} compact />
                          {savingDecision.startsWith(`${item.id}:`) && <p className="flex items-center gap-1 text-xs text-sky-700"><Loader2 size={12} className="animate-spin" /> Saving this file…</p>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {processedFileCount > 0 && !['expired', 'cancelled', 'published'].includes(batch.status) && (
                <section className="space-y-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="intake-master-index-bulk">
                  <div>
                    <h3 className="font-semibold text-slate-900">Master Index — apply to this upload</h3>
                    <p className="mt-1 text-xs text-slate-600">Set shared values in bulk, then customise any file above if it needs something different.</p>
                  </div>
                  <div className="grid gap-4 lg:grid-cols-3">
                    <div className="space-y-2 rounded-lg bg-white p-3">
                      <DocumentTypePicker value={bulkDocumentType} onChange={setBulkDocumentType} compact />
                      <Button type="button" size="sm" onClick={() => applyBatchField('document_type', bulkDocumentType, 'Document type')} disabled={!bulkDocumentType || savingBulkField === 'document_type'}>{savingBulkField === 'document_type' && <Loader2 size={13} className="animate-spin" />} Apply to all files</Button>
                    </div>
                    <div className="space-y-2 rounded-lg bg-white p-3">
                      <KnowledgeBadgePicker value={bulkBadgeIds} onChange={setBulkBadgeIds} user={user} compact />
                      <Button type="button" size="sm" onClick={() => applyBatchField('badges', bulkBadgeIds, 'Badges/topics')} disabled={savingBulkField === 'badges'}>{savingBulkField === 'badges' && <Loader2 size={13} className="animate-spin" />} Apply to all files</Button>
                    </div>
                    <div className="space-y-2 rounded-lg bg-white p-3">
                      <KnowledgeKeywordGroupPicker value={bulkKeywordGroupIds} onChange={setBulkKeywordGroupIds} compact />
                      <Button type="button" size="sm" onClick={() => applyBatchField('keyword_groups', bulkKeywordGroupIds, 'Database tags')} disabled={savingBulkField === 'keyword_groups'}>{savingBulkField === 'keyword_groups' && <Loader2 size={13} className="animate-spin" />} Apply to all files</Button>
                    </div>
                  </div>
                </section>
              )}

              {!!batch.skipped_items?.length && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-amber-900" data-testid="intake-skipped-files">

                  <p className="font-medium">{batch.skipped_items.length} file{batch.skipped_items.length === 1 ? '' : 's'} skipped</p>
                  <ul className="mt-2 space-y-1 text-xs">
                    {batch.skipped_items.map((item, index) => <li key={`${item.relative_path}-${index}`}>{item.filename}: {item.reason}</li>)}
                  </ul>
                </div>
              )}

              {batch.status === 'partial_failure' && (
                <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900" role="alert" data-testid="intake-partial-failure">
                  <XCircle size={18} className="mt-0.5 shrink-0" />
                  <div><p className="font-medium">Some files need attention</p><p className="mt-1 text-xs">Successful files remain in this saved batch. Retry a retryable file or remove it, then continue reviewing the rest.</p></div>
                </div>
              )}

              {['expired', 'cancelled'].includes(batch.status) && (
                <div className="flex items-start gap-3 rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-800" role="status" data-testid="intake-terminal-batch">
                  <AlertCircle size={18} className="mt-0.5 shrink-0" />
                  <div><p className="font-medium">This upload batch is {batch.status}</p><p className="mt-1 text-xs">It cannot be published. Start a new batch to upload these documents again.</p></div>
                </div>
              )}

              {ACTIVE_STATES.has(batch.status) && (
                <span className="sr-only" aria-live="polite">Qflow is reading and classifying this saved batch.</span>
              )}

              {!!decisions.length && (
                <section className="space-y-3" aria-labelledby="intake-decisions-title">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <h3 id="intake-decisions-title" className="font-semibold text-slate-900">Confirm {remainingDecisionCount} item{remainingDecisionCount === 1 ? '' : 's'} before publishing</h3>
                      <p className="text-sm text-slate-500">For {batchFileCount} file{batchFileCount === 1 ? '' : 's'}. Qflow completed {autoFilledCount} other detail{autoFilledCount === 1 ? '' : 's'} automatically.</p>
                    </div>
                    <span className="text-xs text-slate-500">Folder choices apply recursively; files can still be overridden afterwards.</span>
                  </div>
                  {decisionQueue.groups.map(renderFolderDecision)}
                  {decisionQueue.individual.map(renderDecision)}
                </section>
              )}

              {!!autoFilled.length && (
                <section className="overflow-hidden rounded-xl border border-slate-200">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
                    onClick={() => setShowAutoFilled(value => !value)}
                    aria-expanded={showAutoFilled}
                  >
                    {showAutoFilled ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    Details completed automatically ({autoFilled.length})
                  </button>
                  {showAutoFilled && (
                    <div className="overflow-x-auto border-t border-slate-200">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-4 py-2">Document</th><th className="px-4 py-2">Field</th><th className="px-4 py-2">Value</th><th className="px-4 py-2">Score</th><th className="px-4 py-2"><span className="sr-only">Actions</span></th></tr></thead>
                        <tbody className="divide-y divide-slate-100">
                          {autoFilled.map(({ item, key, field }) => {
                            const editId = `${item.id}:${key}`;
                            const editing = editingAutoField === editId;
                            return (
                              <tr key={editId}>
                                <td className="max-w-52 truncate px-4 py-2 text-slate-600">{item.filename}</td>
                                <td className="px-4 py-2 font-medium text-slate-800">{key.replaceAll('_', ' ')}</td>
                                <td className="max-w-72 px-4 py-2 text-slate-700" title={editing ? undefined : fieldValue(field)}>
                                  {editing ? (
                                    <input className="h-8 w-full min-w-36 rounded-md border border-slate-300 px-2 text-sm" value={drafts[editId] ?? ''} onChange={event => setDrafts(previous => ({ ...previous, [editId]: event.target.value }))} aria-label={`Override ${key.replaceAll('_', ' ')} for ${item.filename}`} />
                                  ) : <span className="block truncate">{fieldValue(field)}</span>}
                                </td>
                                <td className="px-4 py-2"><span className={`rounded-full border px-2 py-1 text-xs ${scoreStyle(field.score)}`}>{Math.round((field.score || 0) * 100)}%</span></td>
                                <td className="px-4 py-2 text-right">
                                  {editing ? (
                                    <div className="flex justify-end gap-1">
                                      <Button size="sm" variant="ghost" onClick={() => setEditingAutoField('')}>Cancel</Button>
                                      <Button size="sm" onClick={async () => {
                                        const saved = await decideField({ id: editId, item_id: item.id, field_key: key, value: field.value }, drafts[editId], 'edit');
                                        if (saved) setEditingAutoField('');
                                      }}>Save</Button>
                                    </div>
                                  ) : (
                                    <Button size="sm" variant="ghost" onClick={() => {
                                      setEditingAutoField(editId);
                                      setDrafts(previous => ({ ...previous, [editId]: Array.isArray(field.value) ? field.value.join(', ') : (field.value ?? '') }));
                                    }}>Edit</Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}

              {inlineMessage && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert">
                  <AlertCircle size={17} className="mt-0.5 shrink-0" /> {inlineMessage}
                </div>
              )}

              <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-slate-500">
                  <p>Mode: <span className="font-medium text-slate-700">{feature.automation_mode?.replaceAll('_', ' ')}</span></p>
                  <p>Nothing is indexed until every required decision is made.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={requestStartOver}
                    disabled={publishInProgress || hasPublishedItems}
                    title={hasPublishedItems ? 'Published files must be managed in Knowledge Hub' : undefined}
                  >
                    Discard and start over
                  </Button>
                  <Button onClick={publish} disabled={publishing || ACTIVE_STATES.has(batch.status) || ['expired', 'cancelled', 'published'].includes(batch.status)}>
                    {publishing ? <Loader2 className="animate-spin" /> : <Check />} {publishing ? 'Publishing' : 'Publish and index'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(discardIntent)} onOpenChange={(nextOpen) => {
        if (!nextOpen && !discarding) setDiscardIntent(null);
      }}>
        <AlertDialogContent data-testid="intake-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{discardIntent === 'close' ? 'Keep this upload for later?' : 'Discard this upload and start over?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {batchFileCount} file{batchFileCount === 1 ? ' is' : 's are'} saved with {remainingDecisionCount} confirmation{remainingDecisionCount === 1 ? '' : 's'} remaining. Nothing has been published.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:space-x-0">
            <AlertDialogCancel disabled={discarding}>Continue reviewing</AlertDialogCancel>
            {discardIntent === 'close' && (
              <Button variant="outline" onClick={keepBatchForLater} disabled={discarding || publishInProgress}>Keep for later</Button>
            )}
            <AlertDialogAction
              className="bg-rose-600 text-white hover:bg-rose-700"
              onClick={(event) => {
                event.preventDefault();
                discardBatch();
              }}
              disabled={discarding || publishInProgress}
            >
              {discarding && <Loader2 className="animate-spin" />}
              {discardIntent === 'close' ? 'Discard upload' : 'Discard and start over'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default KnowledgeIntake;
